import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Divider,
  Tooltip,
  LinearProgress,
  useTheme
} from '@mui/material';
import { Grid } from '@mui/material';
import { 
  PlayArrow, 
  Pause,
  SkipNext,
  SkipPrevious,
  Visibility, 
  Mouse, 
  TouchApp, 
  Keyboard, 
  Visibility as VisibilityChangeIcon,
  UnfoldMore,
  Timeline as TimelineIcon,
  CheckCircle,
  Error,
  Warning,
  Cancel,
  ArrowRight
} from '@mui/icons-material';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';

interface SessionRecording {
  id: string;
  sessionId: string;
  startTime: any;
  endTime: any;
  url: string;
  userAgent: string;
  events: any[];
  funnelEvents: any[];
  isActive: boolean;
  totalEvents: number;
  duration: number;
  currentStep: string;
}

interface SessionEvent {
  type: string;
  timestamp: number;
  data: any;
}

const SessionRecordingsPage: React.FC = () => {
  const theme = useTheme();
  const [recordings, setRecordings] = useState<SessionRecording[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecording, setSelectedRecording] = useState<SessionRecording | null>(null);
  const [replayDialogOpen, setReplayDialogOpen] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const [replayError, setReplayError] = useState<string | null>(null);

  useEffect(() => {
    fetchRecordings();
  }, []);

  const fetchRecordings = async () => {
    try {
      const q = query(
        collection(db, 'sessionRecordings'), 
        orderBy('startTime', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const recordingsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SessionRecording[];
      setRecordings(recordingsData);
    } catch (error) {
      console.error('Error fetching recordings:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp.toDate()).toLocaleString();
  };

  const formatDuration = (duration: number) => {
    if (!duration) return 'N/A';
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getSessionStatus = (recording: SessionRecording) => {
    if (recording.isActive) {
      return { label: 'Active', color: 'success' as const };
    } else if (recording.endTime) {
      return { label: 'Completed', color: 'primary' as const };
    } else {
      return { label: 'Incomplete', color: 'warning' as const };
    }
  };

  const handleViewRecording = (recording: SessionRecording) => {
    setSelectedRecording(recording);
    setReplayDialogOpen(true);
    setShowAllEvents(false);
    setReplayError(null);
  };

  const handleCloseReplayDialog = () => {
    setReplayDialogOpen(false);
    setSelectedRecording(null);
    setReplayError(null);
    setShowAllEvents(false);
  };

  const getEventsToShow = () => {
    if (!selectedRecording) return [];
    const filteredEvents = selectedRecording.events.filter(event => event.type !== 'mousemove');
    return showAllEvents ? filteredEvents : filteredEvents.slice(0, 20);
  };

  const getTotalEventsCount = () => {
    if (!selectedRecording) return 0;
    return selectedRecording.events.filter(event => event.type !== 'mousemove').length;
  };

  const getRemainingEventsCount = () => {
    if (!selectedRecording) return 0;
    const filteredEvents = selectedRecording.events.filter(event => event.type !== 'mousemove');
    return Math.max(0, filteredEvents.length - 20);
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'click':
        return <TouchApp color="primary" />;
      case 'mousemove':
        return <Mouse color="action" />;
      case 'input':
        return <Keyboard color="secondary" />;
      case 'scroll':
        return <UnfoldMore color="info" />;
      case 'visibilitychange':
        return <VisibilityChangeIcon color="warning" />;
      default:
        return <TimelineIcon color="disabled" />;
    }
  };

  const getEventColor = (eventType: string) => {
    switch (eventType) {
      case 'click':
        return 'primary';
      case 'mousemove':
        return 'grey';
      case 'input':
        return 'secondary';
      case 'scroll':
        return 'info';
      case 'visibilitychange':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPaletteColor = (colorName: string) => {
    switch (colorName) {
      case 'primary':
        return theme.palette.primary.main;
      case 'secondary':
        return theme.palette.secondary.main;
      case 'info':
        return theme.palette.info.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'error':
        return theme.palette.error.main;
      case 'success':
        return theme.palette.success.main;
      case 'grey':
        return theme.palette.grey[500];
      default:
        return theme.palette.primary.main;
    }
  };

  const getReadableEventDescription = (event: SessionEvent) => {
    const { type, data } = event;
    const elementInfo = data.elementInfo || {};
    
    switch (type) {
      case 'click':
        // Use the captured element information for better descriptions
        if (elementInfo.buttonText) {
          return { action: `Clicked "${elementInfo.buttonText}" button`, icon: '🔘', color: 'primary' };
        } else if (elementInfo.linkText) {
          return { action: `Clicked "${elementInfo.linkText}" link`, icon: '🔗', color: 'info' };
        } else if (elementInfo.textContent && elementInfo.textContent.length > 0) {
          const text = elementInfo.textContent.substring(0, 30);
          return { action: `Clicked "${text}${elementInfo.textContent.length > 30 ? '...' : ''}"`, icon: '👆', color: 'primary' };
        } else if (elementInfo.attributes?.['aria-label']) {
          return { action: `Clicked "${elementInfo.attributes['aria-label']}"`, icon: '🔘', color: 'primary' };
        } else if (elementInfo.tagName === 'button') {
          return { action: 'Clicked button', icon: '🔘', color: 'primary' };
        } else if (elementInfo.tagName === 'a') {
          return { action: 'Clicked link', icon: '🔗', color: 'info' };
        } else if (elementInfo.tagName === 'input') {
          const inputType = elementInfo.attributes?.type || 'text';
          if (inputType === 'checkbox') {
            return { action: 'Toggled checkbox', icon: '☑️', color: 'success' };
          } else if (inputType === 'radio') {
            return { action: 'Selected radio option', icon: '🔘', color: 'primary' };
          } else {
            return { action: 'Clicked input field', icon: '📝', color: 'warning' };
          }
        } else if (elementInfo.tagName === 'select') {
          return { action: 'Opened dropdown menu', icon: '📋', color: 'info' };
        } else {
          // Fallback to path-based detection for older recordings
          const path = data.path || '';
          if (path.includes('button') || path.includes('Button')) {
            return { action: 'Clicked button', icon: '🔘', color: 'primary' };
          } else if (path.includes('menu') || path.includes('Menu')) {
            return { action: 'Opened menu', icon: '📋', color: 'info' };
          } else {
            return { action: 'Clicked page element', icon: '👆', color: 'primary' };
          }
        }
        
      case 'input':
        const value = data.value || '';
        const label = elementInfo.label || '';
        
        if (label) {
          if (label.toLowerCase().includes('email')) {
            return { action: `Entered email: ${value}`, icon: '📧', color: 'info' };
          } else if (label.toLowerCase().includes('phone')) {
            return { action: `Entered phone: ${value}`, icon: '📞', color: 'info' };
          } else if (label.toLowerCase().includes('name')) {
            return { action: `Entered name: ${value}`, icon: '👤', color: 'info' };
          } else if (label.toLowerCase().includes('address')) {
            return { action: `Entered address: ${value}`, icon: '🏠', color: 'info' };
          } else if (label.toLowerCase().includes('vessel') || label.toLowerCase().includes('boat')) {
            return { action: `Entered vessel info: ${value}`, icon: '⛵', color: 'info' };
          } else if (label.toLowerCase().includes('comment') || label.toLowerCase().includes('note')) {
            return { action: `Added comment: ${value.substring(0, 50)}${value.length > 50 ? '...' : ''}`, icon: '💬', color: 'info' };
          } else {
            return { action: `Entered "${label}": ${value}`, icon: '✏️', color: 'info' };
          }
        } else {
          // Fallback to generic input description
          return { action: `Entered text: ${value}`, icon: '✏️', color: 'info' };
        }
        
      case 'scroll':
        return { action: 'Scrolled page', icon: '📜', color: 'grey' };
        
      case 'visibilitychange':
        const state = data.state;
        if (state === 'hidden') {
          return { action: 'Left the page', icon: '👋', color: 'warning' };
        } else {
          return { action: 'Returned to page', icon: '👀', color: 'success' };
        }
        
      default:
        return { action: `${type} event`, icon: '❓', color: 'grey' };
    }
  };

  const simplifyPath = (path: string) => {
    // This function is now less important since we're using getReadableEventDescription
    if (!path) return 'Unknown element';
    
    // Extract meaningful parts from the path
    const parts = path.split(' > ');
    const meaningfulParts = parts.filter(part => 
      !part.includes('MuiBox-root') && 
      !part.includes('css-') &&
      !part.startsWith('div.MuiGrid-root')
    );
    
    if (meaningfulParts.length === 0) {
      return 'Page element';
    }
    
    return meaningfulParts.slice(-2).join(' → ');
  };

  const getSessionStats = (events: SessionEvent[]) => {
    const stats = {
      clicks: events.filter(e => e.type === 'click').length,
      inputs: events.filter(e => e.type === 'input').length,
      scrolls: events.filter(e => e.type === 'scroll').length,
      mouseMoves: events.filter(e => e.type === 'mousemove').length,
      duration: 0
    };

    if (events.length > 0) {
      const firstEvent = Math.min(...events.map(e => e.timestamp));
      const lastEvent = Math.max(...events.map(e => e.timestamp));
      stats.duration = Math.round((lastEvent - firstEvent) / 1000); // in seconds
    }

    return stats;
  };

  const getFunnelStatus = (recording: SessionRecording) => {
    const funnelEvents = recording.funnelEvents || [];
    
    // Check for booking completion
    const completed = funnelEvents.some(event => event.type === 'booking_completed');
    if (completed) {
      return { label: 'Completed', color: 'success' as const };
    }
    
    // Check for errors
    const hasError = funnelEvents.some(event => 
      event.type === 'payment_error' || event.type === 'validation_error'
    );
    if (hasError) {
      return { label: 'Error', color: 'error' as const };
    }
    
    // Check for abandonment
    const abandoned = funnelEvents.some(event => event.type === 'abandoned');
    if (abandoned) {
      return { label: 'Abandoned', color: 'warning' as const };
    }
    
    return { label: 'In Progress', color: 'info' as const };
  };

  const getFunnelSummary = (recording: SessionRecording) => {
    const funnelEvents = recording.funnelEvents || [];
    const summary: string[] = [];
    
    // Get the last error if any
    const lastError = [...funnelEvents]
      .reverse()
      .find(event => event.type === 'payment_error' || event.type === 'validation_error');
    
    if (lastError) {
      if (lastError.type === 'payment_error') {
        summary.push(`Payment Error: ${lastError.data.error?.message || 'Unknown error'}`);
      } else if (lastError.type === 'validation_error') {
        summary.push(`Validation Error: ${lastError.data.fields?.join(', ') || 'Invalid fields'}`);
      }
    }
    
    // Get the last step
    const lastStep = [...funnelEvents]
      .reverse()
      .find(event => event.type === 'step_completed');
    
    if (lastStep) {
      summary.push(`Last Step: ${lastStep.data.step}`);
    }
    
    // Get abandonment reason if any
    const abandoned = funnelEvents.find(event => event.type === 'abandoned');
    if (abandoned) {
      summary.push(`Abandoned at: ${abandoned.data.step || 'Unknown step'}`);
    }
    
    return summary.join(' • ');
  };

  const renderFunnelEvents = (recording: SessionRecording) => {
    const funnelEvents = recording.funnelEvents || [];
    
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Funnel Events
        </Typography>
        <Box sx={{ position: 'relative' }}>
          {funnelEvents.map((event, index) => (
            <Box 
              key={index} 
              sx={{ 
                display: 'flex', 
                mb: 2,
                position: 'relative',
                '&::before': index < funnelEvents.length - 1 ? {
                  content: '""',
                  position: 'absolute',
                  left: 20,
                  top: 40,
                  bottom: -16,
                  width: 2,
                  bgcolor: 'divider'
                } : {}
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: event.type === 'booking_completed' ? 'success.main' :
                          event.type === 'payment_error' || event.type === 'validation_error' ? 'error.main' :
                          event.type === 'abandoned' ? 'warning.main' : 'primary.main',
                  color: 'white',
                  mr: 2
                }}
              >
                {event.type === 'booking_completed' ? <CheckCircle /> :
                 event.type === 'payment_error' ? <Error /> :
                 event.type === 'validation_error' ? <Warning /> :
                 event.type === 'abandoned' ? <Cancel /> :
                 <ArrowRight />}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">
                  {new Date(event.timestamp || 0).toLocaleString()}
                </Typography>
                <Typography>
                  {event.type === 'payment_error' && `Payment Error: ${event.data.error?.message}`}
                  {event.type === 'validation_error' && `Validation Error: ${event.data.fields?.join(', ')}`}
                  {event.type === 'booking_completed' && 'Booking Completed'}
                  {event.type === 'abandoned' && `Abandoned at ${event.data.step}`}
                  {event.type === 'step_completed' && `Completed ${event.data.step}`}
                </Typography>
                {event.data.error?.details && (
                  <Typography variant="body2" color="text.secondary">
                    Details: {JSON.stringify(event.data.error.details)}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Session Recordings
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Funnel Status</TableCell>
              <TableCell>Session Stats</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recordings.map((recording) => {
              const stats = getSessionStats(recording.events);
              const status = getSessionStatus(recording);
              const funnelStatus = getFunnelStatus(recording);
              const funnelSummary = getFunnelSummary(recording);
              return (
                <TableRow key={recording.id}>
                  <TableCell>{formatDate(recording.startTime)}</TableCell>
                  <TableCell>{recording.url}</TableCell>
                  <TableCell>
                    <Chip 
                      size="small" 
                      label={status.label} 
                      color={status.color}
                      variant={recording.isActive ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={funnelStatus.label}
                      color={funnelStatus.color}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      <Chip size="small" label={`${stats.clicks} Clicks`} color="primary" />
                      <Chip size="small" label={`${stats.inputs} Inputs`} color="secondary" />
                      <Chip size="small" label={`${stats.scrolls} Scrolls`} color="info" />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {funnelSummary}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleViewRecording(recording)}
                      color="primary"
                    >
                      <PlayArrow />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={replayDialogOpen}
        onClose={handleCloseReplayDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Session Details</Typography>
            <Box display="flex" gap={1}>
              {!showAllEvents && getRemainingEventsCount() > 0 && (
                <Button
                  variant="contained"
                  onClick={() => setShowAllEvents(true)}
                  startIcon={<UnfoldMore />}
                >
                  Show All {getTotalEventsCount()} Events
                </Button>
              )}
              {showAllEvents && (
                <Button
                  variant="outlined"
                  onClick={() => setShowAllEvents(false)}
                  startIcon={<UnfoldMore />}
                >
                  Show First 20 Events
                </Button>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRecording && (
            <Box>
              {/* Session Info */}
                              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Session Details</Typography>
                      <Typography variant="body2"><strong>URL:</strong> {selectedRecording.url}</Typography>
                      <Typography variant="body2"><strong>Date:</strong> {formatDate(selectedRecording.startTime)}</Typography>
                      <Typography variant="body2"><strong>User Agent:</strong> {selectedRecording.userAgent}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Session Stats</Typography>
                      {(() => {
                        const stats = getSessionStats(selectedRecording.events);
                        return (
                          <Box>
                            <Typography variant="body2"><strong>Duration:</strong> {formatDuration(selectedRecording.duration)}</Typography>
                            <Typography variant="body2"><strong>Total Events:</strong> {selectedRecording.events.length}</Typography>
                            <Typography variant="body2"><strong>Meaningful Events:</strong> {getTotalEventsCount()}</Typography>
                            <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                              <Chip size="small" label={`${stats.clicks} Clicks`} color="primary" />
                              <Chip size="small" label={`${stats.inputs} Inputs`} color="secondary" />
                              <Chip size="small" label={`${stats.scrolls} Scrolls`} color="info" />
                            </Box>
                          </Box>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Event Timeline */}
              <Typography variant="h6" gutterBottom>
                User Journey Timeline
                {!showAllEvents && getRemainingEventsCount() > 0 && (
                  <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
                    (Showing first 20 of {getTotalEventsCount()} events)
                  </Typography>
                )}
                {showAllEvents && (
                  <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
                    (Showing all {getTotalEventsCount()} events)
                  </Typography>
                )}
              </Typography>
              
              <Box sx={{ position: 'relative', pl: 3 }}>
                {getEventsToShow().map((event, index) => {
                  const description = getReadableEventDescription(event);
                  return (
                    <Box key={index} sx={{ position: 'relative', mb: 2 }}>
                      {/* Vertical line */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: -12,
                          top: 0,
                          bottom: 0,
                          width: 2,
                          bgcolor: 'divider',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: -4,
                            top: 0,
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: (theme.palette as any)[description.color]?.main || description.color,
                          }
                        }}
                      />
                      
                      {/* Event content */}
                      <Paper elevation={1} sx={{ p: 2, ml: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box display="flex" alignItems="center" gap={1} flex={1}>
                            <Typography sx={{ fontSize: '20px' }}>{description.icon}</Typography>
                            <Box>
                              <Typography variant="subtitle1" color={`${description.color}.main`} fontWeight="bold">
                                {description.action}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(event.timestamp).toLocaleTimeString()}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        
                        {/* Additional technical details (collapsible) */}
                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '10px' }}>
                            Technical: {simplifyPath(event.data.path)}
                            {event.data.x && event.data.y && ` • Position: (${event.data.x}, ${event.data.y})`}
                          </Typography>
                        </Box>
                      </Paper>
                    </Box>
                  );
                })}
              </Box>

              {!showAllEvents && getRemainingEventsCount() > 0 && (
                <Box textAlign="center" mt={3}>
                  <Button
                    variant="contained"
                    onClick={() => setShowAllEvents(true)}
                    startIcon={<UnfoldMore />}
                    size="large"
                  >
                    View All {getRemainingEventsCount()} Remaining Events
                  </Button>
                </Box>
              )}

              {renderFunnelEvents(selectedRecording)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReplayDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SessionRecordingsPage; 